import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import * as NotifService from '@/lib/notificationService';

export type NotificationPermissionState = 'default' | 'granted' | 'denied';

interface UseNotificationsReturn {
  // Status
  isSupported: boolean;
  isEnabled: boolean;
  permission: NotificationPermissionState;
  isIOS: boolean;
  isInstalledPWA: boolean;
  needsIOSInstall: boolean;
  isDenied: boolean;
  isLoading: boolean;
  
  // Actions
  enable: () => Promise<boolean>;
  disable: () => Promise<boolean>;
  toggle: (enabled: boolean) => Promise<boolean>;
  sendTestNotification: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const { user, profile, updateProfile } = useAuth();
  const [status, setStatus] = useState<NotifService.NotificationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  // Load status
  const loadStatus = useCallback(async () => {
    if (!user) {
      setStatus(null);
      setIsLoading(false);
      return;
    }

    try {
      const newStatus = await NotifService.getStatus(user.id);
      setStatus(newStatus);

      // Auto-sync if needed
      if (newStatus.hasNativeSubscription && !newStatus.hasDbSubscription) {
        console.log('[useNotifications] Auto-syncing...');
        await NotifService.autoSync(user.id);
        // Reload after sync
        const refreshedStatus = await NotifService.getStatus(user.id);
        setStatus(refreshedStatus);
      }
    } catch (error) {
      console.error('[useNotifications] Status load error:', error);
    }

    setIsLoading(false);
  }, [user]);

  // Initial load
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Listen for subscription changes
  useEffect(() => {
    if (!user) return;

    const cleanup = NotifService.listenForChanges(() => {
      console.log('[useNotifications] Subscription changed, refreshing...');
      loadStatus();
    });

    return cleanup;
  }, [user, loadStatus]);

  // Sync permission denial with profile - only when NOT in action
  useEffect(() => {
    if (!user || !profile || !status) return;
    // CRITICAL: Don't sync during an action or while loading
    if (isLoading || isActionInProgress) return;

    // Only sync if permission is truly denied AND we have a DB subscription to clean
    if (status.permission === 'denied' && status.hasDbSubscription) {
      console.log('[useNotifications] Permission denied but has DB subscription, cleaning up...');
      NotifService.unsubscribe(user.id).then(() => {
        updateProfile({ push_notifications_enabled: false });
      });
    }
  }, [user, profile, status, updateProfile, isLoading, isActionInProgress]);

  // Enable notifications
  const enable = useCallback(async (): Promise<boolean> => {
    if (!user || isActionInProgress) return false;
    
    console.log('[useNotifications] Starting enable flow...');
    setIsActionInProgress(true);
    setIsLoading(true);

    try {
      // Check iOS requirements
      if (status?.isIOS && !status?.isInstalledPWA) {
        console.warn('[useNotifications] iOS requires PWA installation');
        return false;
      }

      // Request permission
      const granted = await NotifService.requestPermission();
      if (!granted) {
        console.warn('[useNotifications] Permission denied by user');
        await loadStatus();
        return false;
      }

      console.log('[useNotifications] Permission granted, subscribing...');
      
      // Subscribe
      const success = await NotifService.subscribe(user.id);
      console.log('[useNotifications] Subscribe result:', success);
      
      if (success) {
        // Update profile IMMEDIATELY after successful subscription
        await updateProfile({ push_notifications_enabled: true });
        console.log('[useNotifications] Profile updated with push_notifications_enabled: true');
      }

      await loadStatus();
      return success;
    } catch (error) {
      console.error('[useNotifications] Enable error:', error);
      await loadStatus();
      return false;
    } finally {
      setIsActionInProgress(false);
      setIsLoading(false);
    }
  }, [user, status, updateProfile, loadStatus, isActionInProgress]);

  // Disable notifications
  const disable = useCallback(async (): Promise<boolean> => {
    if (!user || isActionInProgress) return false;
    
    console.log('[useNotifications] Starting disable flow...');
    setIsActionInProgress(true);
    setIsLoading(true);

    try {
      const success = await NotifService.unsubscribe(user.id);
      console.log('[useNotifications] Unsubscribe result:', success);
      
      if (success) {
        await updateProfile({ push_notifications_enabled: false });
        console.log('[useNotifications] Profile updated with push_notifications_enabled: false');
      }

      await loadStatus();
      return success;
    } catch (error) {
      console.error('[useNotifications] Disable error:', error);
      await loadStatus();
      return false;
    } finally {
      setIsActionInProgress(false);
      setIsLoading(false);
    }
  }, [user, updateProfile, loadStatus, isActionInProgress]);

  // Toggle
  const toggle = useCallback(async (enabled: boolean): Promise<boolean> => {
    return enabled ? enable() : disable();
  }, [enable, disable]);

  // Send test notification
  const sendTestNotification = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    const result = await NotifService.sendNotification(
      user.id,
      '🔔 Test',
      'Les notifications fonctionnent correctement !'
    );

    return result.success;
  }, [user]);

  // Refresh status
  const refresh = useCallback(async () => {
    setIsLoading(true);
    await loadStatus();
  }, [loadStatus]);

  // Derived states
  const isSupported = status?.isSupported ?? false;
  const isEnabled = status?.isFullyEnabled ?? false;
  const permission = (status?.permission ?? 'default') as NotificationPermissionState;
  const isIOS = status?.isIOS ?? false;
  const isInstalledPWA = status?.isInstalledPWA ?? false;
  const needsIOSInstall = isIOS && !isInstalledPWA;
  const isDenied = permission === 'denied';

  return {
    isSupported,
    isEnabled,
    permission,
    isIOS,
    isInstalledPWA,
    needsIOSInstall,
    isDenied,
    isLoading,
    enable,
    disable,
    toggle,
    sendTestNotification,
    refresh,
  };
}
